import type { LaddersStyle } from "@bing/engine-ladders";
import {
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { BOARD_ASPECT, FRAME, SQUARE } from "./geometry";
import { pickVariant, pictureScale, warmedVariant, type SceneVariant } from "./prefetch";
import { ScenePicture } from "./ScenePicture";
import { lookFor, type LaddersLook } from "./styles";

/* ------------------------------------------------------------------ *
 * مار پله — the SCENE (card-86; round 2, batch W1): the look's backdrop
 * (a picture, or «اورجینال»'s plain one), the board CENTRED on it, the
 * die's place right above the board. W2 supplies the board (`renderBoard`), the die (`renderDie`) and
 * the strip under it (`footer`). The Bing Games brand is GONE (name and
 * logo — the operator, round 2 item 4).
 *
 * ⚠️ MEASURED, NEVER VIEWPORT-ANCHORED. One ResizeObserver on the scene and
 * the stage feeds ONE pure function, `sceneLayout`, that places the board,
 * then the die, then the picture.
 *
 * ROUND 3 (card-86-r3b, the operator 2026-11-04: «Both background pictures
 * look heavily zoomed in and lose their quality, and parts of the scene are
 * cut off … Never stretch a picture beyond its real resolution. If a
 * screen's shape doesn't match the picture, fill the edges in a way that
 * blends with it instead of cropping the scene»). The picture is no longer
 * raised to COVER: it is CONTAINED — the whole picture, uncropped — in the
 * scene under the top inset (so the temple / pavilion is never under
 * Telegram's header), at most at its widest variant's real px, and drawn
 * from the variant whose px cover the box's device px (`pickVariant`). The
 * rest of the scene — the header strip, a wider screen's sides — is the
 * FILL: the picture itself, blurred at build time with its edges carried
 * outward, stretched by a static `border-image` so its centre lies exactly
 * under the picture and its margins reach every edge (the full-bleed of
 * round 2 item 5, with no white strip and no crop); the picture's edges
 * feather into it. The board's size is today's (the solver is unchanged);
 * it is only lowered, within the slack, to start under the landmark's foot,
 * and the picture slides sideways inside its fill until the spire stands
 * over the 100 cell ({@link PICTURE_SLIDES_TO_100}). Nothing of the picture
 * is drawn over the board any more (round 2's cut-out is gone).
 *
 * ⚠️ NOTHING IS MIRRORED: `dir="ltr"` on the scene, physical left/top
 * only. 100 stays top-left under the temple (a CSS grid would follow
 * `dir="rtl"` and move it top-right, away from the temple).
 *
 * ⚠️ THE SCENE OWNS THE SAFE AREAS (top and bottom insets): the footer
 * must not pad them again. The scene itself is inset 0 — under Telegram's
 * header buttons — and only its column is padded.
 *
 * ⚠️ NOTHING HERE MOVES: no animation, no transition, no runtime blur. The
 * picture's load fade is `ScenePicture`'s (batch P), once.
 * ------------------------------------------------------------------ */

/** What the scene tells its footer: the column's width in CSS px (0 before the first measure).
 *  Measured from the stage, so it never depends on the footer's own height. */
export interface LaddersSceneFit {
  readonly width: number;
}

export interface LaddersSceneProps {
  /** `view.style` — the look (an unknown one draws jungle). */
  readonly style: LaddersStyle;
  /** The board, drawn in the centred board slot at `sizePx` (the square's side). */
  readonly renderBoard: (sizePx: number) => ReactNode;
  /** The die, drawn in its place right ABOVE the board, centred on it: a `sizePx` square, ≥ 44. */
  readonly renderDie: (sizePx: number) => ReactNode;
  /** W2: seat strip, hint, timer — or a function of the column's fit. */
  readonly footer: ReactNode | ((fit: LaddersSceneFit) => ReactNode);
  /** W2: the shell's overlays (exit, chat toggle). */
  readonly children?: ReactNode;
}

/** The board's square never runs closer than this to the column's sides. */
export const SCENE_MARGIN_X = 8;
/** Room between the board (its start pad) and the footer. */
export const SCENE_GAP_BELOW = 6;
/** The board's largest side, on any screen. */
export const SCENE_MAX_SIDE = 520;
/**
 * The top of the stage that W2's header strip owns — the exit and the chat toggle, 40 px buttons
 * 10 px under the safe top inset (as on every table) — in STAGE px: the stage starts 8 px under the
 * inset, so the strip ends at 10 + 40 − 8 = 42, plus a 4 px gap. The die never enters it (at
 * 240×533 the centred die would sit on the chat toggle). Keep in step with W2's strip.
 */
export const SCENE_HEADER_RESERVE = 10 + 40 - 8 + 4;
/** The table column's width (every table's: `max-w-[440px]`). */
export const SCENE_COLUMN_MAX = 440;
/** The die's place: never smaller than this (a thumb's target)… */
export const DIE_SLOT_MIN = 44;
/** …never larger than this, and otherwise this share of the board's side. */
export const DIE_SLOT_MAX = 52;
export const DIE_SLOT_SHARE = 0.13;
/** Between the die's place and the board's frame — at least this; a taller screen gives more. */
export const DIE_GAP = 6;
/**
 * Of the room ABOVE the board (`SLACK_ABOVE_*`), this share goes between the die and the board —
 * the rest between the header strip and the die (the operator, 2026-11-04: «Move the die's area a
 * bit higher, so there's more space between it and the board»).
 */
export const DIE_GAP_SHARE = 0.5;
/** The die stays at least this far right of the temple's box. */
export const DIE_TEMPLE_GAP = 4;
/**
 * Of a tall screen's spare height, this share goes ABOVE the die and the board, the rest UNDER the
 * player strip — the die, the board and the strip stand together, centred…
 */
export const SLACK_ABOVE_SHARE = 0.5;
/** …but never more than this above — the more room above the board, the more the picture grows. */
export const SLACK_ABOVE_MAX = 56;
/**
 * The player strip (the scene's `footer`) sits this far under the board's start pad — raised to
 * it, never left at the bottom of a tall screen (the operator, 2026-11-04: «Move the player panel
 * at the bottom … a bit closer to the board»). Its height is its own (fixed during play).
 */
export const FOOTER_GAP = SCENE_GAP_BELOW;
/** The column's own top padding over the safe inset (CSS px): the stage starts this far under it. */
export const SCENE_PAD_TOP = 8;
/**
 * P3 of round 3 — «keep the 100 cell right under the temple/pavilion»: the contained picture slides
 * sideways inside its fill until the spire's x is over the 100 cell's centre, as far as the fill's
 * room allows (the picture is never cropped). On a phone that is all the room there is: the picture
 * meets the screen's left edge and the whole fill strip is on the right; the spire lands inside the
 * 100 cell on every size checked. `false` centres the picture instead (the spire then stands over
 * the 99 cell on the phones).
 */
export const PICTURE_SLIDES_TO_100 = true;
/**
 * Where the picture meets its fill, its own edge fades into it over this many CSS px (a static mask)
 * — at the top never deeper than the band above the landmark's tip (`band.top`), so the spire is
 * never faded.
 */
export const FEATHER_PX = 16;

export interface SceneBox {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

/** What `sceneLayout` is measured against — CSS px. */
export interface SceneFrame {
  /** The stage: the column between the padded top and the footer. */
  readonly stageW: number;
  readonly stageH: number;
  /** The stage's top-left inside the scene (the full-bleed root). */
  readonly offX: number;
  readonly offY: number;
  /** The whole scene (the viewport, header strip included). */
  readonly sceneW: number;
  readonly sceneH: number;
  /** Device px per CSS px (`devicePixelRatio`): the picture never gets more than its variant has. */
  readonly dpr: number;
}

/** How far each edge of the scene lies past the picture (CSS px, ≥ 0): the fill's border widths. */
export interface SceneGaps {
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly left: number;
}

export interface SceneLayout {
  /** The board's square side (`sizePx`). */
  readonly side: number;
  /** The board element (square + start pad), in stage px. */
  readonly board: SceneBox;
  /** The die's place (a square), in stage px. */
  readonly die: SceneBox;
  /** Source px → CSS px (0 for a look without a picture). */
  readonly scale: number;
  /** The whole picture, in stage px — contained in the scene under the top inset, never cropped. */
  readonly bg: SceneBox | null;
  /** The variant drawn: the narrowest whose px cover the box's device px. */
  readonly variant: SceneVariant | null;
  /** Image px per device px of that variant on this screen (≥ 1: never stretched). */
  readonly sharpness: number;
  /** The scene's edges past the picture — what the fill covers. */
  readonly gaps: SceneGaps | null;
  /** How deep the picture's edge fades into the fill on each side (0 where it meets the screen's). */
  readonly feather: SceneGaps | null;
  /** The landmark's foot (stage px): the board starts at or under it whenever the slack allows. */
  readonly footY: number | null;
  /** The temple's box's right edge (stage px) — the die stays clear of it. */
  readonly templeRight: number | null;
  /** How far the footer is RAISED from the column's bottom, to sit {@link FOOTER_GAP} under the
   *  board (CSS px, ≥ 0) — a static transform: the stage, measured, never moves for it. */
  readonly footerLift: number;
}

/** The die's place for a board `side` px wide. */
export function dieSlotFor(side: number): number {
  return Math.round(Math.min(DIE_SLOT_MAX, Math.max(DIE_SLOT_MIN, side * DIE_SLOT_SHARE)));
}

/**
 * Where everything goes. Pure: the tests drive it at every device size the operator checks.
 *
 *   1. the largest side such that the header strip, the die's place, the board (+ its start pad)
 *      and the gap above the footer fit the stage height, and the square fits the width — the same
 *      for every look (the picture never costs the board a pixel);
 *   2. the board is CENTRED in the column; a tall screen's spare height goes half above
 *      (≤ {@link SLACK_ABOVE_MAX}) and the rest under the player strip, which is RAISED to sit
 *      {@link FOOTER_GAP} under the board; the die sits above the board, centred on it — the room
 *      above shared between its gap to the board and its gap to the header strip — nudged right
 *      only where the temple's box would touch it;
 *   3. a picture look: the picture CONTAINED in the scene under the top inset (whole, uncropped,
 *      at its top), never past its widest variant's real px; slid sideways inside its fill so the
 *      spire stands over the 100 cell ({@link PICTURE_SLIDES_TO_100}); the board LOWERED, within
 *      the slack only, to start under the landmark's foot — and where that is not enough, the
 *      picture made SMALLER until its foot meets the board's top (the temple / pavilion is always
 *      wholly in view); the fill covers every edge past the picture.
 */
export function sceneLayout(frame: SceneFrame, look: LaddersLook): SceneLayout {
  const { stageW, stageH } = frame;
  const minTop = (side: number): number => SCENE_HEADER_RESERVE + dieSlotFor(side) + DIE_GAP;
  const fits = (side: number): boolean =>
    minTop(side) + side * BOARD_ASPECT + SCENE_GAP_BELOW <= stageH;
  let lo = 0;
  let hi = Math.max(0, Math.min(SCENE_MAX_SIDE, stageW - 2 * SCENE_MARGIN_X));
  if (fits(hi)) lo = hi;
  else {
    for (let i = 0; i < 40; i += 1) {
      const mid = (lo + hi) / 2;
      if (fits(mid)) lo = mid;
      else hi = mid;
    }
  }
  const side = Math.floor(lo);
  const dieSize = dieSlotFor(side);
  const height = side * BOARD_ASPECT;
  const slack = Math.max(0, stageH - (minTop(side) + height + SCENE_GAP_BELOW));
  const above = Math.min(slack * SLACK_ABOVE_SHARE, SLACK_ABOVE_MAX);
  const boardLeft = Math.round((stageW - side) / 2);
  const centred = Math.round(boardLeft + (side - dieSize) / 2);
  const dieDrop = DIE_GAP + Math.round(above * DIE_GAP_SHARE);

  /** The board at `top` (stage px): the die above it, the strip raised under it. */
  const place = (top: number) => ({
    board: { left: boardLeft, top, width: side, height },
    dieTop: top - dieDrop - dieSize,
    footerLift: Math.max(0, stageH - (top + height + FOOTER_GAP)),
  });

  if (look.kind !== "picture" || side <= 0) {
    const p = place(Math.round(minTop(side) + above));
    return {
      side,
      board: p.board,
      die: { left: centred, top: p.dieTop, width: dieSize, height: dieSize },
      scale: 0,
      bg: null,
      variant: null,
      sharpness: 0,
      gaps: null,
      feather: null,
      footY: null,
      templeRight: null,
      footerLift: p.footerLift,
    };
  }

  const S = look.scene;
  const variants = look.bg.variants;
  const widest = variants[variants.length - 1]?.w ?? 0;
  // The picture: contained in the scene UNDER the top inset (the stage starts SCENE_PAD_TOP under
  // it), at its top, at most at its widest variant's real px.
  const insetTop = Math.max(0, frame.offY - SCENE_PAD_TOP);
  const availH = Math.max(0, frame.sceneH - insetTop);
  const picTop = insetTop - frame.offY;
  const foot = S.temple ? S.temple.y + S.temple.h : S.board.y;
  const contained = pictureScale(frame.sceneW, availH, S.master, widest, frame.dpr);
  // The landmark's foot: the board starts under it when the slack allows — never at the side's cost.
  const footAt = picTop + foot * contained;
  const usual = minTop(side) + above;
  const p = place(
    footAt <= usual
      ? Math.round(usual)
      : Math.max(
          Math.round(usual),
          Math.min(Math.ceil(footAt), Math.floor(stageH - SCENE_GAP_BELOW - height)),
        ),
  );
  // …and where even the lowest board would still cover it (a Telegram Desktop window: the picture
  // is contained by the height, the board by the width), the PICTURE gives way: smaller, its foot
  // on the board's top edge, the fill taking the rest. The board never shrinks for it.
  const s = Math.min(contained, (p.board.top - picTop) / foot);
  const pw = S.master.w * s;
  const ph = S.master.h * s;
  const footY = picTop + foot * s;
  const sceneLeft = -frame.offX;
  const sceneRoom = Math.max(0, frame.sceneW - pw);
  const cell = side / SQUARE;
  const c100 = boardLeft + (FRAME + 0.5) * cell;
  const want = PICTURE_SLIDES_TO_100 ? c100 - S.band.spireX * s : sceneLeft + sceneRoom / 2;
  const picLeft = Math.min(Math.max(want, sceneLeft), sceneLeft + sceneRoom);
  const bg = { left: picLeft, top: picTop, width: pw, height: ph };
  const templeRight = picLeft + S.band.templeRight * s;
  // Centred on the board — unless the temple's box would touch it: then just clear of it.
  const dieLeft = Math.round(
    Math.min(Math.max(centred, templeRight + DIE_TEMPLE_GAP), boardLeft + side - dieSize),
  );
  const variant = pickVariant(variants, pw * frame.dpr);
  const gapLeft = picLeft - sceneLeft;
  const gaps = {
    top: insetTop,
    right: Math.max(0, frame.sceneW - gapLeft - pw),
    bottom: Math.max(0, availH - ph),
    left: Math.max(0, gapLeft),
  };
  const fade = (gap: number, most = FEATHER_PX): number =>
    gap > 0.5 ? Math.min(FEATHER_PX, most) : 0;
  return {
    side,
    board: p.board,
    die: { left: dieLeft, top: p.dieTop, width: dieSize, height: dieSize },
    scale: s,
    bg,
    variant,
    sharpness: variant && pw > 0 ? variant.w / (pw * frame.dpr) : 0,
    gaps,
    feather: {
      top: fade(gaps.top, Math.floor(S.band.top * s)),
      right: fade(gaps.right),
      bottom: fade(gaps.bottom),
      left: fade(gaps.left),
    },
    footY,
    templeRight,
    footerLift: p.footerLift,
  };
}

interface Measured {
  readonly w: number;
  readonly h: number;
  readonly offX: number;
  readonly offY: number;
  readonly sw: number;
  readonly sh: number;
  readonly dpr: number;
}

const same = (a: Measured, b: Measured): boolean =>
  a.w === b.w &&
  a.h === b.h &&
  a.offX === b.offX &&
  a.offY === b.offY &&
  a.sw === b.sw &&
  a.sh === b.sh &&
  a.dpr === b.dpr;

/** The fill's border widths: the picture's gaps, to 0.01 px. */
export function fillBorder(g: SceneGaps): string {
  const px = (v: number): string => `${Math.round(v * 100) / 100}px`;
  return `${px(g.top)} ${px(g.right)} ${px(g.bottom)} ${px(g.left)}`;
}

/** The picture's feather on each side, as the mask class's custom properties. */
function featherVars(f: SceneGaps): CSSProperties {
  return {
    ["--lad-ft" as string]: `${f.top}px`,
    ["--lad-fr" as string]: `${f.right}px`,
    ["--lad-fb" as string]: `${f.bottom}px`,
    ["--lad-fl" as string]: `${f.left}px`,
  } as CSSProperties;
}

export function LaddersScene({
  style,
  renderBoard,
  renderDie,
  footer,
  children,
}: LaddersSceneProps): ReactNode {
  const look = lookFor(style);
  const sceneRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<Measured | null>(null);
  useLayoutEffect(() => {
    const scene = sceneRef.current;
    const stage = stageRef.current;
    if (!scene || !stage) return undefined;
    // ONE observer, two targets: the scene (the screen) and the stage (the footer, the insets). A
    // callback per resize — never a loop; nothing runs while nothing changes.
    const measure = (): void => {
      const a = scene.getBoundingClientRect();
      const b = stage.getBoundingClientRect();
      const next = {
        w: stage.clientWidth,
        h: stage.clientHeight,
        offX: Math.round((b.left - a.left) * 100) / 100,
        offY: Math.round((b.top - a.top) * 100) / 100,
        sw: scene.clientWidth,
        sh: scene.clientHeight,
        dpr:
          typeof window !== "undefined" && window.devicePixelRatio > 0
            ? window.devicePixelRatio
            : 1,
      };
      setBox((prev) => (prev && same(prev, next) ? prev : next));
    };
    measure();
    if (typeof ResizeObserver !== "function") return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(scene);
    ro.observe(stage);
    return () => ro.disconnect();
  }, []);
  // Laid out once per measure and look — a game's frames re-render the scene, never re-solve it.
  const L = useMemo(
    () =>
      box && box.w > 0 && box.h > 0
        ? sceneLayout(
            {
              stageW: box.w,
              stageH: box.h,
              offX: box.offX,
              offY: box.offY,
              sceneW: box.sw,
              sceneH: box.sh,
              dpr: box.dpr,
            },
            look,
          )
        : null,
    [box, look],
  );
  const fit: LaddersSceneFit = { width: box?.w ?? 0 };
  // NEVER A NARROWER VARIANT once one at least as wide as the box needs is chosen: a later, smaller
  // measure — Telegram reporting its top inset after the first frame of a link-opened table, a
  // Desktop window dragged smaller — would otherwise blank the picture for a fetch of LESS. A wider
  // need steps up; a new look starts afresh — from the preload's file when that one is wide enough.
  const kept = useRef<{ readonly id: string; readonly v: SceneVariant } | null>(null);
  let variant = L?.variant ?? null;
  if (variant) {
    const k = kept.current;
    const held = k && k.id === look.id ? k.v : warmedVariant(look.id);
    if (held && held.w >= variant.w) variant = held;
    kept.current = { id: look.id, v: variant };
  }

  return (
    <div
      ref={sceneRef}
      dir="ltr"
      data-testid="ladders-scene"
      data-style={look.id}
      className={`ladders-scene ladders-scene--${look.kind}`}
      style={{
        position: "relative",
        height: "var(--game-h)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        // A picture look: its edge colours — the base under the fill (which rides in the bundle,
        // so it is there at once) and the picture (`ScenePicture` fades it in once it has loaded).
        // «اورجینال»: its class's plain backdrop.
        background: look.kind === "picture" ? look.placeholder : undefined,
      }}
    >
      {/* THE FILL: the picture blurred at build time, its edges carried outward — its centre
          stretched exactly under the picture, its margins over every edge past it (the header
          strip, a wider screen's sides). A static border-image: no runtime blur, nothing moves. */}
      {look.kind === "picture" && look.bg.fill && L && L.gaps ? (
        <div
          data-testid="ladders-fill"
          aria-hidden
          className="ladders-scene__fill"
          style={{
            position: "absolute",
            inset: 0,
            boxSizing: "border-box",
            pointerEvents: "none",
            borderStyle: "solid",
            borderColor: "transparent",
            borderWidth: fillBorder(L.gaps),
            borderImageSource: `url("${look.bg.fill}")`,
            borderImageSlice: `${look.scene.fill.margin} fill`,
            borderImageRepeat: "stretch",
          }}
        />
      ) : null}
      <div
        style={{
          position: "relative",
          flex: "1 1 auto",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          width: "100%",
          maxWidth: SCENE_COLUMN_MAX,
          margin: "0 auto",
          paddingTop: `calc(${SCENE_PAD_TOP}px + max(env(safe-area-inset-top), var(--tg-safe-top, 0px)))`,
          paddingBottom: "var(--game-safe-bottom)",
          boxSizing: "border-box",
        }}
      >
        <div
          ref={stageRef}
          data-testid="ladders-stage"
          style={{ position: "relative", flex: "1 1 auto", minHeight: 0 }}
        >
          {L ? (
            <>
              {look.kind === "picture" && L.bg && variant && L.feather ? (
                <ScenePicture
                  jpg={variant.jpg}
                  {...(variant.webp ? { webp: variant.webp } : {})}
                  box={L.bg}
                  className="ladders-scene__bg"
                  vars={featherVars(L.feather)}
                  testId="ladders-bg"
                />
              ) : null}
              <div
                data-testid="ladders-board-slot"
                style={{
                  position: "absolute",
                  left: L.board.left,
                  top: L.board.top,
                  width: L.board.width,
                  height: L.board.height,
                }}
              >
                {renderBoard(L.side)}
              </div>
              {/* The die's place: right above the board, centred on it — a clear place of its own. */}
              <div
                data-testid="ladders-die-place"
                className="ladders-die-place"
                style={{
                  position: "absolute",
                  left: L.die.left,
                  top: L.die.top,
                  width: L.die.width,
                  height: L.die.height,
                }}
              >
                {renderDie(L.die.width)}
              </div>
            </>
          ) : null}
        </div>
        <div
          data-testid="ladders-footer"
          style={{
            position: "relative",
            flex: "none",
            zIndex: 3,
            // Raised to sit under the board — a static transform, so the measured stage never moves.
            transform: L && L.footerLift > 0 ? `translateY(${-L.footerLift}px)` : undefined,
          }}
        >
          {typeof footer === "function" ? footer(fit) : footer}
        </div>
      </div>
      {children}
    </div>
  );
}
